import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Sidebar } from '../../shared/components/sidebar/sidebar';
import { Navbar } from '../../shared/components/navbar/navbar';
import { Button } from '../../shared/components/button/button';
import { Toast as ToastComponent } from '../../shared/components/toast/toast';
import { Toast } from '../../shared/services/toast';
import { GuardrailConfigModal } from '../../shared/components/guardrail-config-modal/guardrail-config-modal';
import { GuardrailTestModal } from '../../shared/components/guardrail-test-modal/guardrail-test-modal';
import { EmptyState } from '../../shared/components/empty-state/empty-state';
import { GuardrailConfigService, GuardrailDefinition } from '../../core/services/guardrail-config';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-guardrail-config',
  standalone: true,
  imports: [CommonModule, Sidebar, Navbar, Button, ToastComponent, GuardrailConfigModal, GuardrailTestModal, EmptyState],
  templateUrl: './guardrail-config.html',
  styleUrl: './guardrail-config.css'
})
export class GuardrailConfigPage {
  private guardrailService = inject(GuardrailConfigService);
  private toast = inject(Toast);

  guardrails = this.guardrailService.guardrails;
  activeGuardrail = signal<GuardrailDefinition | null>(null);
  testModalOpen = signal(false);
  saving = signal(false);

  enabledCount(): number {
    return this.guardrails().filter(g => g.enabled).length;
  }

  summaryFor(g: GuardrailDefinition): string {
    switch (g.kind) {
      case 'entities':
      case 'categories': {
        const { checked, total } = this.guardrailService.selectedCount(g);
        return `${checked} of ${total} ${g.kind === 'entities' ? 'entities' : 'categories'} selected`;
      }
      case 'threshold':
        return `Sensitivity: ${g.threshold}`;
      case 'llm':
        return `Model: ${g.model ?? 'gpt-4.1-mini'}, threshold: ${g.confidenceThreshold ?? 0.7}`;
      case 'text':
        return g.text ? 'Custom scope defined' : 'No scope defined yet';
      case 'url-list':
        return `Allows ${g.urlAllowList?.length ?? 0} URL${(g.urlAllowList?.length ?? 0) === 1 ? '' : 's'}`;
      case 'none':
        return 'No user-configurable options';
      default:
        return 'No summary available';
    }
  }

  openConfig(g: GuardrailDefinition) {
    this.activeGuardrail.set(g);
  }

  closeModal() {
    this.activeGuardrail.set(null);
  }

  onToggleEnabled(g: GuardrailDefinition, event: Event) {
    event.stopPropagation();
    this.guardrailService.setEnabled(g.id, !g.enabled);
  }

  onSave(updated: GuardrailDefinition) {
    this.guardrailService.saveGuardrail(updated);
    this.activeGuardrail.set(null);
    this.toast.show(`${updated.name} settings saved.`, 'success');
  }

  async saveConfiguration() {
    this.saving.set(true);
    try {
      await this.guardrailService.saveConfiguration();
      this.toast.show('Guardrail configuration saved successfully.', 'success');
    } catch (err) {
      const msg = err instanceof HttpErrorResponse
        ? (err.error?.message || 'Failed to save configuration.')
        : 'Failed to save configuration.';
      this.toast.show(msg, 'error');
    } finally {
      this.saving.set(false);
    }
  }

  openTest() {
    this.testModalOpen.set(true);
  }

  closeTest() {
    this.testModalOpen.set(false);
  }

  inputGuardrails(): GuardrailDefinition[] {
    return this.guardrails().filter(g => g.stage === 'input');
  }

  outputGuardrails(): GuardrailDefinition[] {
    return this.guardrails().filter(g => g.stage === 'output');
  }
}
